import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  useEffect(() => {
    console.log('🔐 AuthProvider: Setting up authentication...')
    
    // Check if we're on a password reset redirect
    const checkPasswordResetRedirect = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const hash = window.location.hash
      
      // Check for password reset in URL params or hash
      if (urlParams.get('reset') === 'true' || (hash && hash.includes('type=recovery'))) {
        console.log('🔑 Password reset redirect detected')
        setIsPasswordRecovery(true)
      }
    }

    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('🔍 Getting initial session...')
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('❌ Error getting session:', error.message)
        } else if (session) {
          console.log('✅ Session found:', session.user.email)
          setSession(session)
          setUser(session.user)
        } else {
          console.log('ℹ️ No existing session found')
        }
      } catch (error) {
        console.error('❌ Error in getInitialSession:', error)
      } finally {
        setLoading(false)
      }
    }

    // Check password reset redirect first
    checkPasswordResetRedirect()
    
    // Then get session
    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔄 Auth state changed:', event, session?.user?.email || 'no user')
        
        // Handle specific auth events
        switch (event) {
          case 'SIGNED_IN':
            console.log('✅ User signed in:', session?.user?.email)
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)
            // Clear password recovery state on sign in
            setIsPasswordRecovery(false)
            // Clear any existing URL params/hash after successful login
            if (window.location.search || window.location.hash) {
              window.history.replaceState({}, document.title, window.location.pathname)
            }
            break
          case 'SIGNED_OUT':
            console.log('👋 User signed out')
            setSession(null)
            setUser(null)
            setLoading(false)
            setIsPasswordRecovery(false)
            break
          case 'PASSWORD_RECOVERY':
            console.log('🔑 Password recovery event detected')
            setSession(session)
            setUser(session?.user ?? null)
            setIsPasswordRecovery(true)
            setLoading(false)
            break
          case 'TOKEN_REFRESHED':
            console.log('🔄 Token refreshed for:', session?.user?.email)
            // Only update session, don't change user to prevent unnecessary re-renders
            setSession(session)
            break
          case 'USER_UPDATED':
            console.log('👤 User updated:', session?.user?.email)
            setSession(session)
            setUser(session?.user ?? null)
            // Clear password recovery state after password update
            setIsPasswordRecovery(false)
            break
          default:
            console.log('🔄 Auth event:', event)
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)
        }
      }
    )

    return () => {
      console.log('🧹 Cleaning up auth listener')
      subscription.unsubscribe()
    }
  }, [])

  const signOut = async () => {
    try {
      console.log('👋 Signing out user...')
      setLoading(true)
      
      // Use 'local' scope to ensure we sign out from this browser only
      // 'global' scope can sometimes fail silently if there are token issues
      const { error } = await supabase.auth.signOut({ scope: 'local' })
      
      if (error) {
        console.error('❌ Error signing out:', error.message)
        // Even if there's an error, try to clear local state
        console.log('⚠️ Forcing local state clear despite error...')
      }
      
      // Always clear local storage when signing out
      localStorage.removeItem('petHeavenPetId')
      localStorage.removeItem('petHeavenPetName')
      localStorage.removeItem('petHeavenAngelImage')
      
      // Manually clear the auth state in case onAuthStateChange doesn't fire
      // This ensures the UI updates even if Supabase has issues
      console.log('🧹 Manually clearing auth state...')
      setSession(null)
      setUser(null)
      setIsPasswordRecovery(false)
      
      console.log('✅ User signed out successfully')
    } catch (error) {
      console.error('❌ Error in signOut:', error)
      // Force clear state even on exception
      setSession(null)
      setUser(null)
      setIsPasswordRecovery(false)
    } finally {
      setLoading(false)
    }
  }

  const clearPasswordRecovery = () => {
    setIsPasswordRecovery(false)
    // Clear URL params
    if (window.location.search || window.location.hash) {
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }

  const value = {
    user,
    session,
    loading,
    signOut,
    isPasswordRecovery,
    clearPasswordRecovery
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
